import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AccountService } from './account.service';
import { TenantHostService } from './tenant-host.service';
import { BrandingService } from './branding.service';

/**
 * Guards the tenant-scoped auth pages (login / register). Resolves the tenant
 * from the current origin first:
 *  - resolved  => store its id and allow the page.
 *  - not found => the academy doesn't exist, so we don't show its login/register;
 *    redirect to the public portal with `?academy=<subdomain>` so it can explain.
 */
export const tenantResolveGuard: CanActivateFn = (): Observable<boolean | UrlTree> => {
  const account = inject(AccountService);
  const router = inject(Router);
  const host = inject(TenantHostService);
  const branding = inject(BrandingService);

  const toPortal = (): UrlTree => {
    const sub = host.getSubdomain();
    return router.createUrlTree(['/portal'], {
      queryParams: sub ? { academy: sub } : {},
    });
  };

  return account.getTenentIdByURL().pipe(
    map((res) => {
      if (res.isSuccess && res.value?.id) {
        localStorage.setItem('tenantId', res.value.id);
        branding.apply(res.value);
        return true;
      }
      return toPortal();
    }),
    catchError(() => of(toPortal())),
  );
};
